import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { name: "", title: "", email: "", phone: "", certification: "", certification_expiry: "", status: "active" };

const inputCls =
  "w-full bg-white border border-[#E8E6E1] rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]";

function ExpiryBadge({ dateStr }) {
  if (!dateStr) return <span className="text-xs text-[#57605A]">—</span>;
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  let cls = "bg-[#E6F0EB] text-[#34745C]";
  let label = new Date(dateStr).toLocaleDateString();
  if (days < 0) {
    cls = "bg-[#FCEAE8] text-[#B54D40]";
    label = `Expired ${new Date(dateStr).toLocaleDateString()}`;
  } else if (days <= 30) {
    cls = "bg-[#FDF3E1] text-[#C48231]";
    label = `${days}d left`;
  }
  return <span className={`${cls} px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap`}>{label}</span>;
}

export default function CaregiversPage() {
  const { user } = useAuth();
  const [caregivers, setCaregivers] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/caregivers").then((r) => setCaregivers(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  if (user.role === "family") return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, title: c.title || "", email: c.email || "", phone: c.phone || "",
      certification: c.certification || "", certification_expiry: c.certification_expiry || "",
      status: c.status || "active",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, certification_expiry: form.certification_expiry || null };
    try {
      if (editing) await api.put(`/caregivers/${editing.id}`, payload);
      else await api.post("/caregivers", payload);
      toast.success(editing ? "Caregiver updated" : "Caregiver added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save caregiver");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from the caregiver roster?`)) return;
    try {
      await api.delete(`/caregivers/${c.id}`);
      toast.success("Caregiver removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove caregiver");
    }
  };

  return (
    <Layout>
      <div data-testid="caregivers-page" className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Caregiver Management</p>
            <h1 className="mt-2 text-3xl font-heading font-light tracking-tight">
              Your <span className="font-bold text-[#2E5C50]">care team</span>
            </h1>
            <p className="mt-2 text-[#57605A]">Track caregivers, certifications and renewal dates — expiring credentials surface as alerts automatically.</p>
          </div>
          <button
            data-testid="add-caregiver-btn"
            onClick={openAdd}
            className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add caregiver
          </button>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden">
          {caregivers === null ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" strokeWidth={1.5} />
            </div>
          ) : caregivers.length === 0 ? (
            <div data-testid="empty-caregivers" className="p-12 text-center">
              <Users className="w-8 h-8 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
              <p className="mt-3 text-[#57605A]">No caregivers yet. Add your first caregiver or load demo data from the dashboard.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E6E1] bg-[#F9F7F4]">
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Name</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A] hidden md:table-cell">Contact</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A] hidden md:table-cell">Certification</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Cert. expiry</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {caregivers.map((c) => (
                  <tr key={c.id} data-testid={`caregiver-row-${c.id}`} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F9F7F4]/60 transition-colors duration-150">
                    <td className="px-5 py-3">
                      <p className="font-medium text-[#1C211F]">
                        {c.name}
                        {c.is_demo && <span className="ml-2 text-xs text-[#C07A60] font-semibold">DEMO</span>}
                      </p>
                      <p className="text-xs text-[#57605A]">{c.title}</p>
                    </td>
                    <td className="px-5 py-3 text-[#57605A] hidden md:table-cell">
                      <p>{c.email}</p>
                      <p className="text-xs">{c.phone}</p>
                    </td>
                    <td className="px-5 py-3 text-[#57605A] hidden md:table-cell">{c.certification || "—"}</td>
                    <td className="px-5 py-3"><ExpiryBadge dateStr={c.certification_expiry} /></td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                        c.status === "active" ? "bg-[#E6F0EB] text-[#34745C]" : "bg-[#F9F7F4] border border-[#E8E6E1] text-[#57605A]"
                      }`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`edit-caregiver-${c.id}`}
                        onClick={() => openEdit(c)}
                        className="inline-flex p-1.5 text-[#57605A] hover:text-[#2E5C50] transition-colors duration-150"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`delete-caregiver-${c.id}`}
                        onClick={() => remove(c)}
                        className="inline-flex p-1.5 text-[#57605A] hover:text-[#B54D40] transition-colors duration-150"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F9F7F4] border-[#E8E6E1] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit caregiver" : "Add caregiver"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 mt-2">
            <input data-testid="caregiver-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="caregiver-title-input" placeholder="Title (e.g. Home Health Aide)" value={form.title} onChange={set("title")} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="caregiver-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="caregiver-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <input data-testid="caregiver-certification-input" placeholder="Certification number" value={form.certification} onChange={set("certification")} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Cert. expiry</label>
                <input data-testid="caregiver-expiry-input" type="date" value={form.certification_expiry} onChange={set("certification_expiry")} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Status</label>
                <select data-testid="caregiver-status-select" value={form.status} onChange={set("status")} className={`${inputCls} mt-1`}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <button
              data-testid="save-caregiver-btn"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              {editing ? "Save changes" : "Add caregiver"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
